import React, { useState } from 'react';
import { Upload, Button, Card, Progress, message, Space, Typography, Tag, Select } from 'antd';
import { InboxOutlined, FileTextOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { uploadFiles, getAnalysisStatus } from '../api';
import { useQuote } from '../context/QuoteContext';

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;

function autoMatch(cadFileNames, imgFileName) {
  const imgBase = imgFileName.replace(/\.[^/.]+$/, "").toLowerCase();
  let bestIndex = -1;
  let bestScore = 0;
  cadFileNames.forEach((cadName, idx) => {
    const cadBase = cadName.replace(/\.[^/.]+$/, "").toLowerCase();
    if (cadBase === imgBase) {
      bestScore = 100;
      bestIndex = idx;
    } else if (cadBase.includes(imgBase) || imgBase.includes(cadBase)) {
      const score = Math.min(cadBase.length, imgBase.length);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = idx;
      }
    }
  });
  return bestIndex;
}

export default function UploadPage() {
  const { dispatch } = useQuote();
  const navigate = useNavigate();

  const [parts, setParts] = useState([]);
  const [uploadingAll, setUploadingAll] = useState(false);

  const addPartGroup = (cadFile) => {
    setParts(prev => [...prev, {
      cadFile: cadFile,
      imgFiles: [],
      taskId: null,
      status: 'idle',
      result: null,
      progress: 0,
    }]);
  };

  const handleCadSelected = (files) => {
    files.forEach(file => addPartGroup(file));
    message.success(`已添加 ${files.length} 个零件`);
  };

  const handleImgSelected = (files) => {
    if (parts.length === 0) {
      message.warning('请先上传 3D 文件');
      return;
    }
    setParts(prev => {
      const updated = [...prev];
      const cadNames = updated.map(p => p.cadFile.name);
      files.forEach(imgFile => {
        const matchIndex = autoMatch(cadNames, imgFile.name);
        const targetIndex = matchIndex >= 0 ? matchIndex : 0;
        updated[targetIndex].imgFiles = [...(updated[targetIndex].imgFiles || []), imgFile];
      });
      return updated;
    });
    message.info('2D文件已自动匹配，可在列表中调整');
  };

  const moveImgFile = (imgFile, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setParts(prev => {
      const updated = [...prev];
      updated[fromIndex].imgFiles = updated[fromIndex].imgFiles.filter(f => f.name !== imgFile.name);
      updated[toIndex].imgFiles = [...updated[toIndex].imgFiles, imgFile];
      return updated;
    });
  };

  const handleSubmitAll = async () => {
    if (parts.length === 0) {
      message.warning('请至少添加一个零件');
      return;
    }
    setUploadingAll(true);
    const currentParts = [...parts];
    for (let i = 0; i < currentParts.length; i++) {
      const part = currentParts[i];
      const formData = new FormData();
      formData.append('files', part.cadFile);
      part.imgFiles.forEach(f => formData.append('files', f));
      try {
        const { data } = await uploadFiles(formData);
        setParts(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], taskId: data.task_id, status: 'processing' };
          return updated;
        });
        pollPart(i, data.task_id);
      } catch (err) {
        setParts(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], status: 'failed' };
          return updated;
        });
      }
    }
    setUploadingAll(false);
  };

  const pollPart = async (index, taskId) => {
    try {
      const { data } = await getAnalysisStatus(taskId);
      if (data.status === 'completed') {
        setParts(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'completed',
            result: data.result,
            taskId: taskId,
            progress: 100,
          };
          if (updated.every(p => p.status === 'completed')) {
            Promise.resolve().then(() => {
              dispatch({ type: 'SET_PARTS', payload: updated });
              navigate('/parameters');
            });
          }
          return updated;
        });
      } else if (data.status === 'failed') {
        setParts(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], status: 'failed' };
          return updated;
        });
      } else {
        setParts(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            progress: (prev[index].progress || 0) + 10,
          };
          return updated;
        });
        setTimeout(() => pollPart(index, taskId), 2000);
      }
    } catch (err) {
      setParts(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status: 'failed' };
        return updated;
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined style={{ color: 'green' }} />;
      case 'processing': return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      case 'failed': return <CloseCircleOutlined style={{ color: 'red' }} />;
      default: return null;
    }
  };

  return (
    <Card title="上传多个零件图纸">
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>3D 图纸 (STEP, IGES, STL) - 可多选</Text>
          <Dragger
            multiple
            accept=".step,.stp,.iges,.igs,.stl"
            beforeUpload={(file) => {
              handleCadSelected([file]);
              return false;
            }}
            showUploadList={false}
            disabled={uploadingAll || parts.some(p => p.status === 'processing')}
            style={{ marginTop: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽上传 3D 文件</p>
            <p className="ant-upload-hint">可一次性选择多个文件，每个文件视为一个零件</p>
          </Dragger>
        </div>

        <div>
          <Text strong>2D 图纸 (PDF, PNG, JPG) (可选，会自动按名称匹配)</Text>
          <Dragger
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            beforeUpload={(file) => {
              handleImgSelected([file]);
              return false;
            }}
            showUploadList={false}
            disabled={uploadingAll || parts.length === 0 || parts.some(p => p.status === 'processing')}
            style={{ marginTop: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <FileTextOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽上传 2D 文件（自动匹配对应3D零件）</p>
            <p className="ant-upload-hint">会根据文件名智能匹配，也可在列表中手动调整</p>
          </Dragger>
        </div>

        {parts.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>已添加的零件（点击2D文件可重新分配）</Text>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 8 }}>
              {parts.map((part, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '8px 0', borderBottom: idx < parts.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong>{part.cadFile.name}</Text>
                      {getStatusIcon(part.status)}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {part.imgFiles.map((imgFile, imgIdx) => (
                        <div key={imgIdx} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                          <Tag color="blue">{imgFile.name}</Tag>
                          <Select
                            size="small"
                            value={idx}
                            style={{ width: 120 }}
                            onChange={(val) => moveImgFile(imgFile, idx, val)}
                          >
                            {parts.map((p, i) => (
                              <Option key={i} value={i}>{`零件 ${i+1}: ${p.cadFile.name.substring(0, 15)}...`}</Option>
                            ))}
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {part.status === 'processing' && <Progress size="small" percent={part.progress} style={{ width: 100 }} />}
                    <Button danger size="small" onClick={() => {
                      setParts(prev => prev.filter((_, i) => i !== idx));
                    }} disabled={part.status === 'processing'}>移除零件</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          type="primary"
          size="large"
          block
          onClick={handleSubmitAll}
          loading={uploadingAll}
          disabled={parts.length === 0 || parts.some(p => p.status === 'processing')}
        >
          提交全部解析
        </Button>
      </Space>
    </Card>
  );
}
